/**
 * ProjectCaseList - Premium styled table of cases with completion status
 *
 * Shows individual cases within a project with:
 * - Patient ID, Accession
 * - Progress bar with gradient styling
 * - Status badge (Unlabeled/Partial/Complete)
 * - Filter toggle for unlabeled/all
 * - Click row to navigate to case
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCasesQuery, CaseStatus } from "../../hooks/useLabelingQueries";
import { FaChevronRight, FaCheck, FaSearch } from "../../utils/icons";

interface ProjectCaseListProps {
  projectId: string;
}

export function ProjectCaseList({ projectId }: ProjectCaseListProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"unlabeled" | "all">("unlabeled");
  const { data, isLoading, error } = useCasesQuery(projectId, filter);

  const handleCaseClick = (caseStatus: CaseStatus, index: number) => {
    navigate(
      `/labeling?project=${encodeURIComponent(projectId)}&case=${index}&study=${encodeURIComponent(caseStatus.study_instance_uid)}`,
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-8">
        <div className="w-5 h-5 border-2 border-border-strong border-t-primary rounded-full animate-spin" />
        <span className="text-sm text-fg-muted">Loading cases...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="w-10 h-10 rounded-full bg-warn-tint flex items-center justify-center mx-auto mb-3">
          <span className="text-warn">!</span>
        </div>
        <div className="text-sm text-warn">Failed to load cases</div>
      </div>
    );
  }

  if (!data) return null;

  const unlabeledCount = data.cases.filter((c) => c.completion_status === "unlabeled").length;
  const partialCount = data.cases.filter((c) => c.completion_status === "partial").length;
  const completeCount = data.cases.filter((c) => c.completion_status === "complete").length;
  const unassessableCount = data.cases.filter((c) => c.completion_status === "unassessable").length;
  const excludedCount = data.cases.filter((c) => c.completion_status === "excluded").length;
  const holdCount = data.cases.filter((c) => c.completion_status === "technical_hold").length;
  const allCount = data.total_cases;

  return (
    <div className="space-y-4">
      {/* Header with filter toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaSearch className="w-3.5 h-3.5 text-fg-muted" />
          <span className="text-sm text-fg-muted">Cases</span>
        </div>
        <div className="flex items-center gap-2 p-1 rounded-lg sidebar-btn">
          <button
            onClick={() => setFilter("unlabeled")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
              filter === "unlabeled"
                ? "bg-primary text-white shadow-xs"
                : "text-fg-muted hover:text-white"
            }`}
          >
            Unlabeled ({filter === "unlabeled" ? data.filtered_cases : unlabeledCount})
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
              filter === "all"
                ? "bg-primary text-white shadow-xs"
                : "text-fg-muted hover:text-white"
            }`}
          >
            All ({allCount})
          </button>
        </div>
      </div>

      {/* Case list */}
      {data.cases.length === 0 ? (
        <div className="text-center py-8 rounded panel-premium">
          {filter === "unlabeled" ? (
            <>
              <div className="w-12 h-12 rounded-full bg-signal-tint flex items-center justify-center mx-auto mb-3">
                <FaCheck className="w-5 h-5 text-signal-text-tint" />
              </div>
              <div className="text-signal-text-tint font-medium mb-1">All cases labeled!</div>
              <div className="text-sm text-fg-muted">Great work on completing this project</div>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center mx-auto mb-3">
                <FaSearch className="w-5 h-5 text-fg-muted" />
              </div>
              <div className="text-fg-muted">No cases found</div>
            </>
          )}
        </div>
      ) : (
        <div className="rounded overflow-hidden panel-premium p-0">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_80px_80px_24px] items-center px-4 py-3 text-xs font-medium text-fg-muted uppercase tracking-wider border-b border-premium-subtle">
            <div>Patient</div>
            <div className="text-center">
              {data.cases[0]?.annotators_required != null ? "Annotators" : "Progress"}
            </div>
            <div className="text-center">Status</div>
            <div></div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-border">
            {data.cases.map((caseStatus, index) => (
              <CaseRow
                key={caseStatus.case_id}
                caseStatus={caseStatus}
                defaultAnnotatorsRequired={data.default_annotators_required}
                onClick={() => handleCaseClick(caseStatus, index)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Summary footer */}
      {data.cases.length > 0 && (
        <div className="flex items-center justify-between text-xs text-fg-muted px-1">
          <span>{data.filtered_cases} cases shown</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-signal" />
              {completeCount} complete
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-fg-dim" />
              {partialCount} in progress
            </span>
            {unassessableCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-border-strong" />
                {unassessableCount} unassessable
              </span>
            )}
            {excludedCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-border-strong" />
                {excludedCount} excluded
              </span>
            )}
            {holdCount > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-border-strong" />
                {holdCount} on hold
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-border-strong" />
              {unlabeledCount} pending
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

interface CaseRowProps {
  caseStatus: CaseStatus;
  defaultAnnotatorsRequired: number;
  onClick: () => void;
}

function CaseRow({ caseStatus, defaultAnnotatorsRequired, onClick }: CaseRowProps) {
  // Admin mode: backend sends annotators_required for admin users
  const isAdminView = caseStatus.annotators_required != null;
  const annotatorsCompleted = caseStatus.annotators_completed ?? 0;
  const annotatorsRequired = caseStatus.annotators_required ?? defaultAnnotatorsRequired;

  const progressPercent = isAdminView
    ? Math.min(100, (annotatorsCompleted / annotatorsRequired) * 100)
    : caseStatus.labels_required > 0
      ? (caseStatus.labels_completed / caseStatus.labels_required) * 100
      : 0;

  const statusConfig = {
    complete: {
      bg: "bg-signal-tint",
      text: "text-signal-text-tint",
      border: "border-signal-stroke",
      label: "Done",
      progressColor: "bg-signal",
    },
    partial: {
      bg: "bg-surface-2",
      text: "text-fg",
      border: "border-border-strong",
      label: "In Progress",
      progressColor: "bg-fg-dim",
    },
    unlabeled: {
      bg: "bg-surface-2",
      text: "text-fg-muted",
      border: "border-border",
      label: "Pending",
      progressColor: "bg-border-strong",
    },
    skipped: {
      bg: "bg-surface",
      text: "text-fg-muted",
      border: "border-border",
      label: "Skipped",
      progressColor: "bg-border-strong",
    },
    unassessable: {
      bg: "bg-surface-2",
      text: "text-fg",
      border: "border-border-strong",
      label: "Unassessable",
      progressColor: "bg-border-strong",
    },
    excluded: {
      bg: "bg-surface",
      text: "text-fg-muted",
      border: "border-border",
      label: "Excluded",
      progressColor: "bg-border-strong",
    },
    technical_hold: {
      bg: "bg-surface-2",
      text: "text-fg",
      border: "border-border-strong",
      label: "On hold",
      progressColor: "bg-border-strong",
    },
  };

  // Non-canonicalized studies cannot be loaded — backend returns 423 on
  // /metadata. Surface this state instead of showing a generic "Pending"
  // and letting the click fail. See web-demo-backend#258.
  const isMigrated = caseStatus.migrated !== false; // default true if absent (older API)
  const config = !isMigrated
    ? {
        bg: "bg-bg-2",
        text: "text-fg-muted",
        border: "border-border",
        label: "Migration pending",
        progressColor: "bg-border",
      }
    : statusConfig[caseStatus.completion_status] || statusConfig.unlabeled;

  const handleClick = () => {
    if (!isMigrated) return; // no-op; user can't open a non-canonical study
    onClick();
  };

  return (
    <div
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isMigrated && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={isMigrated ? 0 : -1}
      aria-disabled={!isMigrated}
      data-testid="case-row"
      data-migrated={isMigrated}
      className={`grid grid-cols-[1fr_80px_80px_24px] items-center px-4 py-3 transition-all duration-200 group ${
        isMigrated ? "cursor-pointer hover:bg-white/3" : "cursor-not-allowed opacity-50"
      }`}
      title={
        !isMigrated
          ? "This study has not been migrated to canonical storage yet. It cannot be loaded until migration completes."
          : undefined
      }
    >
      {/* Patient Info */}
      <div className="min-w-0">
        <div className="text-sm text-fg font-medium truncate group-hover:text-white transition-colors">
          {caseStatus.patient_id || "Unknown"}
        </div>
        {caseStatus.accession && (
          <div className="text-xs text-fg-muted truncate mt-0.5">{caseStatus.accession}</div>
        )}
      </div>

      {/* Progress */}
      <div className="flex flex-col items-center gap-1">
        <div className="w-8 h-1 progress-track">
          <div
            className={`h-full rounded-full transition-all duration-300 ${config.progressColor}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="text-[10px] text-fg-muted tabular-nums">
          {isAdminView
            ? `${annotatorsCompleted}/${annotatorsRequired}`
            : `${caseStatus.labels_completed}/${caseStatus.labels_required}`}
        </span>
      </div>

      {/* Status Badge */}
      <div className="flex justify-center">
        <span
          className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${config.bg} ${config.text} ${config.border}`}
          title={
            isAdminView && caseStatus.completed_by?.length
              ? `Completed by: ${caseStatus.completed_by.join(", ")}`
              : undefined
          }
        >
          {config.label}
        </span>
      </div>

      {/* Arrow */}
      <div className="flex justify-center">
        <FaChevronRight className="w-3 h-3 text-fg-muted group-hover:text-signal-text-tint transition-colors" />
      </div>
    </div>
  );
}

export default ProjectCaseList;
